import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { ApiError } from '@/lib/api';
import { useApi } from '@/lib/api-context';

import { flowCapabilitiesResponseSchema, type FlowCapabilities } from './types';

// Mutaciones del flujo. Cada acción golpea su endpoint real (el guardián de permisos
// es el backend, no el `enabled` del front) e invalida las capacidades para refrescar
// las acciones disponibles tras avanzar el estado.

const DOCS = '/api/v1/quality/documents';

interface TransitionVars {
  flowInstanceId: string;
  toStatus: string;
  reason?: string;
}
interface ReturnVars {
  flowInstanceId: string;
  toStatus: string;
  reason: string;
}
interface ApproveVars {
  entryId: string;
  targetVersion: number;
  effectiveDate: string;
}
interface DecommissionVars {
  entryId: string;
  reason?: string;
}
interface EntryVars {
  entryId: string;
}
interface FlowVars {
  flowInstanceId: string;
}

export interface FlowActions {
  transition: UseMutationResult<unknown, Error, TransitionVars>;
  returnDocument: UseMutationResult<unknown, Error, ReturnVars>;
  /** G2-D: devuelve el estado del flujo tras la firma (o null si no se pudo releer). */
  approve: UseMutationResult<FlowCapabilities | null, Error, ApproveVars>;
  decommission: UseMutationResult<unknown, Error, DecommissionVars>;
  startModification: UseMutationResult<unknown, Error, EntryVars>;
  /** G2: conformidad del revisor asignado. */
  conformity: UseMutationResult<unknown, Error, FlowVars>;
  /** G2: Calidad cierra la aprobación con las firmas registradas. */
  closeApproval: UseMutationResult<unknown, Error, FlowVars>;
}

export function useFlowActions(): FlowActions {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['flow-capabilities'] });
    void qc.invalidateQueries({ queryKey: ['flow-history'] });
    // La ficha y la lista muestran el estado y sus acciones: no deben quedar viejas.
    void qc.invalidateQueries({ queryKey: ['dossier'] });
    void qc.invalidateQueries({ queryKey: ['master-list'] });
    // G4: la firma abre la capacitación y cambia el stepper y la ficha.
    void qc.invalidateQueries({ queryKey: ['training-stage'] });
    // La vista del documento pasa de la versión vigente al borrador y al revés.
    void qc.invalidateQueries({ queryKey: ['online-view'] });
  };

  // Un rechazo por estado o asignación (403/409) significa que la pantalla quedó vieja:
  // se recarga para que los botones reflejen el estado real (UX-ERR-01).
  const refreshOnReject = (err: Error): void => {
    if (err instanceof ApiError && (err.status === 403 || err.status === 409)) {
      refresh();
    }
  };

  const transition = useMutation({
    mutationFn: async ({ flowInstanceId, toStatus, reason }: TransitionVars) => {
      const body = reason === undefined ? { toStatus } : { toStatus, reason };
      return api.post(`${DOCS}/flows/${flowInstanceId}/transition`, body);
    },
    onSuccess: refresh,
    onError: refreshOnReject,
  });

  const returnDocument = useMutation({
    mutationFn: async ({ flowInstanceId, toStatus, reason }: ReturnVars) =>
      api.post(`${DOCS}/flows/${flowInstanceId}/return`, { toStatus, reason }),
    onSuccess: refresh,
    onError: refreshOnReject,
  });

  const approve = useMutation({
    mutationFn: async ({
      entryId,
      targetVersion,
      effectiveDate,
    }: ApproveVars): Promise<FlowCapabilities | null> => {
      await api.post(
        '/api/v1/signatures',
        {
          target_module: 'master_list_entries',
          target_id: entryId,
          target_version: targetVersion,
          purpose: 'approve',
          effective_date: effectiveDate,
        },
        { idempotencyKey: crypto.randomUUID() },
      );
      // G2-D: el estado del flujo DESPUÉS de la firma (cuántas faltan, si quedó sellada),
      // para que el aviso diga la verdad. La firma ya se hizo: si la relectura falla o
      // trae otra forma, no es un error de la firma.
      try {
        const raw = await api.get<unknown>(`${DOCS}/${entryId}/flow`);
        const parsed = flowCapabilitiesResponseSchema.safeParse(raw);
        return parsed.success ? parsed.data.data : null;
      } catch {
        return null;
      }
    },
    onSuccess: refresh,
    onError: refreshOnReject,
  });

  const decommission = useMutation({
    mutationFn: async ({ entryId, reason }: DecommissionVars) => {
      const body = reason === undefined ? {} : { reason };
      return api.post(`${DOCS}/${entryId}/decommission`, body);
    },
    onSuccess: refresh,
  });

  const startModification = useMutation({
    mutationFn: async ({ entryId }: EntryVars) =>
      api.post(`${DOCS}/${entryId}/flow`, { kind: 'modificacion' }),
    onSuccess: refresh,
  });

  const conformity = useMutation({
    mutationFn: async ({ flowInstanceId }: FlowVars) =>
      api.post(`${DOCS}/flows/${flowInstanceId}/review/conformity`, {}),
    onSuccess: refresh,
    onError: refreshOnReject,
  });

  const closeApproval = useMutation({
    mutationFn: async ({ flowInstanceId }: FlowVars) =>
      api.post(`${DOCS}/flows/${flowInstanceId}/approval/close`, {}),
    onSuccess: refresh,
    onError: refreshOnReject,
  });

  return {
    transition,
    returnDocument,
    approve,
    decommission,
    startModification,
    conformity,
    closeApproval,
  };
}
